import type {
  NormalizedThinkingEvent,
  ThinkingPhase,
  ThinkingMetrics,
  MessageExecutionStatus,
} from './types';

let _seqCounter = 0;
function nextId(): string {
  _seqCounter += 1;
  return `te-${Date.now()}-${_seqCounter}`;
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v.trim() : fallback;
}

function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

function readable(s: string): string {
  return s.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const TOOL_DISPLAY_NAMES: Record<string, string> = {
  get_patient_context: 'Loading patient context',
  get_patient_demographics: 'Loading demographics',
  get_medications: 'Fetching medications',
  get_conditions: 'Fetching conditions',
  get_observations: 'Fetching lab results',
  get_encounters: 'Fetching encounters',
  get_allergies: 'Fetching allergies',
  get_procedures: 'Fetching procedures',
  get_immunizations: 'Fetching immunizations',
  get_diagnostic_reports: 'Fetching diagnostic reports',
  get_care_plans: 'Fetching care plans',
  get_redflags: 'Checking red flags',
  get_latest_assessment: 'Loading latest assessment',
  run_simulation: 'Running clinical simulation',
  get_draft_note: 'Drafting clinical note',
  get_draft_plan: 'Drafting care plan',
  run_export_pdf: 'Generating PDF export',
  run_export_csv: 'Generating CSV export',
  query_clinical_evidence: 'Query clinical evidence',
  http_rag: 'Clinical evidence retrieval',
};

function friendlyToolName(toolId: string): string {
  return TOOL_DISPLAY_NAMES[toolId] || `Running ${readable(toolId)}`;
}

function classifyPhase(eventName: string): ThinkingPhase {
  if (eventName.startsWith('guardrail.')) return 'safety';
  if (eventName.startsWith('planner.')) return 'planning';
  if (eventName.startsWith('orchestrator.tool')) return 'tools';
  if (eventName.startsWith('orchestrator.')) return 'planning';
  if (eventName.startsWith('multiagent.') || eventName.startsWith('simulation.')) return 'agents';
  if (eventName === 'session.started') return 'system';
  if (eventName.startsWith('thinking.step_timing')) return 'system';
  if (eventName === 'response.metadata' || eventName === 'stream.end') return 'finalization';
  return 'system';
}

function classifyStepTimingPhase(stepKey: string): ThinkingPhase {
  if (stepKey.startsWith('guardrail.')) return 'safety';
  if (stepKey.startsWith('interpreter.') || stepKey.startsWith('planner.')) return 'planning';
  if (stepKey.startsWith('tool.') || stepKey.startsWith('pipeline.')) return 'tools';
  if (stepKey.startsWith('response.') || stepKey.startsWith('final.')) return 'finalization';
  if (stepKey.startsWith('orchestrator.step-final')) return 'finalization';
  if (stepKey.startsWith('orchestrator.')) return 'tools';
  return 'system';
}

function stepTimingLabel(stepKey: string, rawLabel: string): string {
  // Prefer backend-provided label (AI-generated or contextual) over any fallback
  if (rawLabel && rawLabel.trim()) return rawLabel.trim();

  const toolMatch = stepKey.match(/^tool\.(.+)$/);
  if (toolMatch) return friendlyToolName(toolMatch[1]);

  const labelMap: Record<string, string> = {
    'session.started': 'Session started',
    'guardrail.input': 'Safety check',
    'guardrail.output': 'Output safety check',
    'interpreter.route_classification': 'Interpreting request',
    'interpreter.intent': 'Classifying intent',
    'interpreter.extract': 'Extracting instruction',
    'interpreter.tool_selection': 'Selecting tools',
    'interpreter.fast_path': 'Fast path routing',
    'pipeline.patient_history': 'Loading patient history',
    'planner.build_plan': 'Building execution plan',
    'orchestrator.step-final': 'Preparing final response',
    'response.generate': 'Generating response',
  };

  return labelMap[stepKey] || rawLabel || capitalize(readable(stepKey));
}

export function normalizeThinkingEvent(
  eventName: string,
  data: Record<string, unknown>,
  messageId: string,
): NormalizedThinkingEvent {
  const now = new Date().toISOString();

  switch (eventName) {
    case 'session.started':
      return {
        id: nextId(),
        messageId,
        phase: 'system',
        title: 'Session started',
        description: str(data.session_id as string, 'New session'),
        status: 'success',
        severity: 'info',
        startedAt: str(data.timestamp as string, now),
        durationMs: num(data.duration_ms),
        metadata: {},
        raw: data,
      };

    case 'guardrail.input.checked':
      return {
        id: nextId(),
        messageId,
        phase: 'safety',
        title: data.passed === true ? 'Safety check passed' : 'Safety review required',
        description: data.passed === true ? 'Message passed safety filters' : 'Message flagged by safety filters',
        status: data.passed === true ? 'success' : 'error',
        severity: data.passed === true ? 'info' : 'critical',
        startedAt: now,
        durationMs: num(data.duration_ms),
        metadata: { passed: data.passed },
        raw: data,
      };

    case 'guardrail.output.checked':
      return {
        id: nextId(),
        messageId,
        phase: 'safety',
        title: data.passed === true ? 'Output check passed' : 'Output review required',
        description: data.passed === true ? 'Response cleared safety filters' : str(data.action_taken as string, 'Response flagged'),
        status: data.passed === true ? 'success' : 'warning',
        severity: data.passed === true ? 'info' : 'warning',
        startedAt: now,
        durationMs: num(data.duration_ms),
        metadata: { passed: data.passed, action_taken: data.action_taken },
        raw: data,
      };

    case 'planner.status_update': {
      const msg = readable(str(data.message as string) || str(data.event as string) || str(data.status as string));
      const status = str(data.status as string).toLowerCase();
      const progressPercent = num(data.progress_percent);
      const estimatedRemainingMs = num(data.estimated_remaining_ms);
      const stepName = str(data.step_name as string);
      const sk = stepName ? `planner.${stepName}` : '_planner_unknown';
      const rawPayload =
        typeof data === 'object' && data !== null
          ? ({ ...data, step_key: sk } as Record<string, unknown>)
          : ({ step_key: sk } as Record<string, unknown>);
      return {
        id: nextId(),
        messageId,
        phase: 'planning',
        title: capitalize(msg || 'Planning'),
        description: str(data.step_name as string),
        status: status === 'done' || status === 'completed' ? 'success' : status === 'failed' ? 'error' : 'running',
        severity: status === 'failed' ? 'error' : 'info',
        startedAt: now,
        durationMs: num(data.duration_ms),
        progressPercent,
        estimatedRemainingMs,
        metadata: { step_name: data.step_name },
        raw: rawPayload,
      };
    }

    case 'planner.plan_ready':
      return {
        id: nextId(),
        messageId,
        phase: 'planning',
        title: `Execution plan ready (${data.step_count ?? '?'} steps)`,
        description: 'Plan has been generated',
        status: 'success',
        severity: 'info',
        startedAt: now,
        metadata: { step_count: data.step_count },
        raw: data,
      };

    case 'orchestrator.tool_called': {
      const toolName = str(data.tool_id as string) || str(data.tool as string) || str(data.tool_name as string);
      const stepKey = toolName ? `tool.${toolName}` : '';
      const rawPayload =
        typeof data === 'object' && data !== null
          ? ({ ...data, step_key: stepKey || undefined } as Record<string, unknown>)
          : ({ step_key: stepKey || undefined } as Record<string, unknown>);
      return {
        id: nextId(),
        messageId,
        phase: 'tools',
        title: friendlyToolName(toolName),
        description: str(data.input_summary as string),
        status: 'running',
        severity: 'info',
        startedAt: now,
        toolName: toolName || undefined,
        toolCategory: classifyToolCategory(toolName),
        metadata: { step_id: data.step_id },
        raw: rawPayload,
      };
    }

    case 'orchestrator.tool_result': {
      const toolName = str(data.tool_id as string) || str(data.tool as string) || str(data.tool_name as string);
      const resultStatus = str(data.status as string).toLowerCase();
      const processingMs = num(data.processing_time_ms);
      const stepKey = toolName ? `tool.${toolName}` : '';
      const rawPayload =
        typeof data === 'object' && data !== null
          ? ({ ...data, step_key: stepKey || undefined } as Record<string, unknown>)
          : ({ step_key: stepKey || undefined } as Record<string, unknown>);
      return {
        id: nextId(),
        messageId,
        phase: 'tools',
        title: resultStatus === 'success'
          ? `${friendlyToolName(toolName)} — done`
          : `${friendlyToolName(toolName)} — ${resultStatus || 'finished'}`,
        description: str(data.result_summary as string),
        status: resultStatus === 'success' ? 'success' : resultStatus === 'timeout' ? 'warning' : resultStatus === 'partial' ? 'warning' : 'error',
        severity: resultStatus === 'success' ? 'info' : resultStatus === 'error' ? 'error' : 'warning',
        startedAt: now,
        durationMs: processingMs,
        toolName: toolName || undefined,
        toolCategory: classifyToolCategory(toolName),
        metadata: { step_id: data.step_id, result_summary: data.result_summary },
        raw: rawPayload,
      };
    }

    case 'orchestrator.update': {
      const msg = str(data.message as string);
      const status = str(data.status as string).toLowerCase();
      const humanReadable: Record<string, string> = {
        'executing call_tool': 'Running tool',
        'executing final_response': 'Preparing final response',
        'executing invoke_external_agent': 'Calling external agent',
        'executing ehr_write': 'Executing EHR action',
      };
      const normalized = msg.toLowerCase().replace(/\s+/g, ' ').trim();
      let title = humanReadable[normalized] || '';
      let toolName: string | undefined;
      if (!title) {
        const execMatch = normalized.match(/^executing\s+(.+)$/);
        if (execMatch) {
          const rawTool = execMatch[1].replace(/_/g, ' ');
          title = `Executing ${capitalize(rawTool)}`;
          toolName = execMatch[1];
        } else {
          title = capitalize(msg || 'Processing');
        }
      }
      return {
        id: nextId(),
        messageId,
        phase: classifyPhase(eventName),
        title,
        description: str(data.source as string),
        status: status === 'completed' ? 'success' : status === 'failed' ? 'error' : 'running',
        severity: status === 'failed' ? 'error' : 'info',
        startedAt: now,
        durationMs: num(data.duration_ms),
        progressPercent: typeof data.progress === 'number' ? Math.round(data.progress * 100) : undefined,
        toolName,
        metadata: { source: data.source },
        raw: data,
      };
    }

    case 'simulation.running':
      return {
        id: nextId(),
        messageId,
        phase: 'agents',
        title: 'Simulation running',
        description: str(data.scenario as string, 'Running clinical simulation'),
        status: 'running',
        severity: 'info',
        startedAt: now,
        metadata: {},
        raw: data,
      };

    case 'multiagent.specialtyagentcalled':
      return {
        id: nextId(),
        messageId,
        phase: 'agents',
        title: `Consulting ${str(data.agent_name as string, 'specialist')}`,
        description: (() => {
          const edge = [str(data.source as string), str(data.destination as string)]
            .filter(Boolean)
            .join(' → ');
          return edge || str(data.specialty as string, 'Specialist consultation');
        })(),
        status: 'running',
        severity: 'info',
        startedAt: now,
        agentName: str(data.agent_name as string) || undefined,
        metadata: {
          rag_corpus: data.rag_corpus,
          source: data.source,
          destination: data.destination,
          index: data.index,
          total: data.total,
        },
        raw: data,
      };

    case 'multiagent.routing':
      return {
        id: nextId(),
        messageId,
        phase: 'agents',
        title: 'Multi-specialty routing',
        description: str(data.rationale as string, 'Selecting parallel specialist agents'),
        status: 'running',
        severity: 'info',
        startedAt: now,
        toolName: 'specialty_router',
        toolCategory: 'analysis',
        metadata: {
          specialties: data.specialties,
          rag_filters: data.rag_filters,
          parallel: data.parallel,
          source: data.source,
        },
        raw: data,
      };

    case 'multiagent.parallel_specialties_complete':
      return {
        id: nextId(),
        messageId,
        phase: 'agents',
        title: 'Parallel specialists complete',
        description: `${num(data.count) ?? '?'} agent(s) ready for synthesis`,
        status: 'success',
        severity: 'info',
        startedAt: now,
        toolName: 'specialty_synthesis',
        toolCategory: 'analysis',
        metadata: {
          specialties_completed: data.specialties_completed,
          source: data.source,
          destination: data.destination,
        },
        raw: data,
      };

    case 'multiagent.evidencegathered':
      return {
        id: nextId(),
        messageId,
        phase: 'agents',
        title: 'Evidence gathered',
        description: `${num(data.evidence_count) ?? '?'} evidence items collected`,
        status: 'success',
        severity: 'info',
        startedAt: now,
        metadata: { evidence_count: data.evidence_count },
        raw: data,
      };

    case 'multiagent.recommendationready':
      return {
        id: nextId(),
        messageId,
        phase: 'agents',
        title: 'Recommendation ready',
        description: str(data.summary as string, 'Clinical recommendation generated'),
        status: 'success',
        severity: 'info',
        startedAt: now,
        metadata: { confidence: data.confidence },
        raw: data,
      };

    case 'multiagent.rag_complete':
      return {
        id: nextId(),
        messageId,
        phase: 'agents',
        title: 'Clinical reference lookup',
        description: `${num(data.source_count) ?? 0} source${(num(data.source_count) ?? 0) === 1 ? '' : 's'} retrieved`,
        status: 'success',
        severity: 'info',
        startedAt: now,
        toolName: 'march_rag_retrieval',
        toolCategory: 'knowledge',
        metadata: { source_count: data.source_count, grounding_score: data.grounding_score },
        raw: data,
      };

    case 'multiagent.reasoning_complete':
      return {
        id: nextId(),
        messageId,
        phase: 'agents',
        title: 'Multi-agent clinical reasoning',
        description: `${num(data.evidence_count) ?? '?'} evidence items → iterative validation`,
        status: 'success',
        severity: 'info',
        startedAt: now,
        toolName: 'multi_agent_reasoning',
        toolCategory: 'analysis',
        metadata: { evidence_count: data.evidence_count, iterative_valid: data.iterative_valid },
        raw: data,
      };

    case 'thinking.step_timing': {
      const stepKey = str(data.step_key as string);
      const label = str(data.label as string);
      const status = str(data.status as string).toLowerCase();
      const low = label.toLowerCase();
      const degraded =
        low.includes('timed out') ||
        low.includes('timeout') ||
        low.includes('temporarily unavailable') ||
        low.includes('did not complete') ||
        low.includes('incomplete');
      const thinkingStatus =
        status === 'completed' ? (degraded ? 'warning' : 'success') : 'running';
      const thinkingSeverity = degraded ? 'warning' : 'info';
      return {
        id: nextId(),
        messageId,
        phase: classifyStepTimingPhase(stepKey),
        title: stepTimingLabel(stepKey, label),
        description: label.trim() ? label : stepKey,
        status: thinkingStatus,
        severity: thinkingSeverity,
        startedAt: now,
        durationMs: num(data.duration_ms),
        metadata: { step_key: stepKey },
        raw: data,
      };
    }

    case 'thinking.complete':
      return {
        id: nextId(),
        messageId,
        phase: 'finalization',
        title: 'Analysis complete',
        description: 'Preparing response',
        status: 'success',
        severity: 'info',
        startedAt: now,
        metadata: { handoff_to: data.handoff_to },
        raw: data,
      };

    default:
      return {
        id: nextId(),
        messageId,
        phase: classifyPhase(eventName),
        title: capitalize(readable(eventName) || 'System event'),
        description: JSON.stringify(data).slice(0, 120),
        status: 'success',
        severity: 'info',
        startedAt: now,
        metadata: {},
        raw: data,
      };
  }
}

function classifyToolCategory(toolName: string): string | undefined {
  if (!toolName) return undefined;
  const lower = toolName.toLowerCase();
  if (lower.includes('patient') || lower.includes('demographics')) return 'data';
  if (lower.includes('medication') || lower.includes('condition') || lower.includes('observation')
    || lower.includes('allergy') || lower.includes('encounter') || lower.includes('procedure')
    || lower.includes('immunization') || lower.includes('care_plan') || lower.includes('diagnostic')
    || lower.includes('fhir')) return 'data';
  if (lower.includes('export') || lower.includes('pdf') || lower.includes('csv')) return 'export';
  if (lower.includes('risk') || lower.includes('mitigation')) return 'analysis';
  if (lower.includes('rag') || lower.includes('knowledge')) return 'knowledge';
  return 'tool';
}

export function computeMetrics(
  events: NormalizedThinkingEvent[],
  firstFinalTokenAt?: string,
  finalCompletedAt?: string,
): ThinkingMetrics {
  const sessionEvent = events.find((e) => e.phase === 'system' && e.title === 'Session started');
  const sessionStartAt = sessionEvent?.startedAt;
  const firstThinkingEventAt = events[0]?.startedAt;

  const now = new Date().toISOString();
  const endTime = finalCompletedAt || now;

  const totalElapsedMs = sessionStartAt
    ? Math.max(0, new Date(endTime).getTime() - new Date(sessionStartAt).getTime())
    : 0;

  const thinkingEnd = firstFinalTokenAt || endTime;
  const totalThinkingMs = sessionStartAt
    ? Math.max(0, new Date(thinkingEnd).getTime() - new Date(sessionStartAt).getTime())
    : 0;

  const totalFinalStreamMs = firstFinalTokenAt && finalCompletedAt
    ? Math.max(0, new Date(finalCompletedAt).getTime() - new Date(firstFinalTokenAt).getTime())
    : 0;

  const toolDurations: Array<{ toolName: string; durationMs: number }> = [];
  for (const e of events) {
    if (e.toolName && e.durationMs != null && e.status !== 'running') {
      toolDurations.push({ toolName: e.toolName, durationMs: e.durationMs });
    }
  }

  const slowestTool = toolDurations.length > 0
    ? toolDurations.reduce((a, b) => (a.durationMs >= b.durationMs ? a : b)).toolName
    : undefined;

  const allDurations = events.filter((e) => e.durationMs != null);
  const slowestStep = allDurations.length > 0
    ? allDurations.reduce((a, b) => ((a.durationMs ?? 0) >= (b.durationMs ?? 0) ? a : b)).title
    : undefined;

  const avgTool = toolDurations.length > 0
    ? toolDurations.reduce((sum, t) => sum + t.durationMs, 0) / toolDurations.length
    : 0;

  return {
    sessionStartAt,
    firstThinkingEventAt,
    firstFinalTokenAt,
    thinkingCompletedAt: firstFinalTokenAt ? thinkingEnd : undefined,
    finalCompletedAt,
    totalElapsedMs,
    totalThinkingMs,
    totalFinalStreamMs,
    toolDurations,
    slowestStep,
    slowestTool,
    averageToolDuration: Math.round(avgTool),
  };
}

export function deriveExecutionStatus(
  events: NormalizedThinkingEvent[],
  isStreaming: boolean,
  thinkingComplete: boolean,
): MessageExecutionStatus {
  if (events.some((e) => e.status === 'blocked')) return 'blocked';
  if (events.some((e) => e.status === 'cancelled')) return 'cancelled';
  if (events.some((e) => e.status === 'expired')) return 'expired';

  const hasError = events.some((e) => e.severity === 'error' || e.severity === 'critical');
  const hasWarning = events.some((e) => e.severity === 'warning');

  if (!isStreaming && thinkingComplete) {
    if (hasError) return 'failed';
    if (hasWarning) return 'partial';
    return 'completed';
  }

  if (isStreaming) return 'running';

  if (hasError) return 'failed';
  if (hasWarning) return 'partial';
  return 'completed';
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.round(seconds % 60);
  return `${minutes}m ${remainder}s`;
}
